/**
 * Channels screen — the Channex-backed OTA connectivity console.
 *
 * A genuine, provider-backed distribution surface. Every connected/active
 * state, mapping count, booking, sync time and commission figure comes from a
 * real backend endpoint; nothing is fabricated. A channel is only shown as
 * connected/active when its backend connection row reports the matching status
 * (`active`). Where a real value is unavailable we render an em dash ("—").
 *
 * Views (driven by state + `window.location.pathname`):
 *   Landing  (`/app/channels`)         — property selector, KPIs, connected
 *                                         channel cards, empty/readiness states,
 *                                         and the connect catalogue.
 *   Connect  (`/app/channels/connect`) — a full-size embedded Channex connect
 *                                         page (breadcrumb, steps, prerequisite
 *                                         card, iframe). Its URL is minted by
 *                                         the backend; the client never builds
 *                                         it and the Channex key never reaches
 *                                         the browser.
 *   Detail   (in-screen)               — a tabbed Channel Detail view
 *                                         (Overview / Mapping / Bookings /
 *                                         Pricing & Commission / Activity).
 *
 * SSR-safe: the initial render is a pure loading state. Every network call and
 * every `Date`/`window` read runs inside an effect or an event handler. The
 * current path is held in state, seeded from `window.location.pathname` inside
 * an effect and kept fresh via `popstate`.
 *
 * Money arrives as integer-minor STRINGS and is rendered via
 * `formatMoney(Number(x), currency)`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import { OtaLogo } from "../OtaLogo.js";
import {
  apiChannexActivity,
  apiChannexBookingEvents,
  apiChannexBootstrapImport,
  apiChannexBootstrapStatus,
  apiChannexCatalogue,
  apiChannexChannelDetail,
  apiChannexConnect,
  apiChannexRemoveChannel,
  apiChannexDiagnostics,
  apiChannexOverview,
  apiChannexPrep,
  apiChannexProcessAvailability,
  apiChannexSyncInventory,
  apiChannexProperties,
  apiChannexProvision,
  apiChannexReadiness,
  apiChannexReconcile,
  apiChannexReprocessBookingEvent,
  apiChannexSyncBookings,
  apiChannexTestConnection,
  apiListBookings,
  asError,
  formatMoney,
  type BookingRow,
  type BookingSyncResult,
  type ChannelConnectionRow,
  type ChannelRemoveResult,
  type ChannexRemovalState,
  type ChannelPrep,
  type ChannexActivityRow,
  type ChannexBookingEvent,
  type ChannexBootstrapState,
  type ChannexBootstrapStatus,
  type ChannexBootstrapImportResult,
  type ExistingBookingsDeclared,
  type ChannexChannelDetail,
  type ChannexConnectionStage,
  type ChannexConnectionStatus,
  type ChannexDiagnostics,
  type FullSyncResultRow,
  type ChannexOverview,
  type ChannexPropertyOption,
  type ConnectResult,
  type OtaCatalogueItem,
  type ProvisionResult,
  type ReadinessResult,
} from "../services.js";

/** Fallback currency when an overview/booking omits one. */
const FALLBACK_CURRENCY = "INR";

/** The two paths this screen renders for. */
const PATH_LANDING = "/app/channels";
const PATH_CONNECT = "/app/channels/connect";

// ===========================================================================
// Status + formatting helpers
// ===========================================================================

/** Human labels for the connection status. */
const STATUS_LABEL: Record<ChannexConnectionStatus, string> = {
  not_connected: "Not connected",
  setup_required: "Setup required",
  connecting: "Connecting",
  active: "Active",
  paused: "Paused",
  attention: "Attention",
  sync_error: "Sync error",
  disconnected: "Disconnected",
};

/**
 * Map a connection status to the shared status-pill modifier class. Only
 * `active` maps to the success (ok) tone — never fabricate a healthy state.
 */
function statusPill(status: ChannexConnectionStatus): string {
  switch (status) {
    case "active":
      return "avos-status avos-status--ok";
    case "attention":
      return "avos-status avos-status--attention";
    case "sync_error":
      return "avos-status avos-status--critical";
    case "disconnected":
      return "avos-status avos-status--critical";
    case "paused":
      return "avos-status avos-status--attention";
    case "connecting":
    case "setup_required":
    case "not_connected":
    default:
      return "avos-status avos-status--pending";
  }
}

/** True when a connection is mid-setup and should offer "Continue setup". */
function isInProgress(status: ChannexConnectionStatus): boolean {
  return (
    status === "setup_required" ||
    status === "connecting" ||
    status === "attention"
  );
}

/**
 * Human label for the in-flight Safe-Remove lifecycle, shown on the card while
 * a channel is being deactivated/removed. Never shows "Removed" until Channex
 * removal actually succeeded (the API returns finalState, and a 'removed' row is
 * physically gone, so it never reaches the card — included only for exhaustiveness).
 */
const REMOVAL_LABEL: Record<ChannexRemovalState, string> = {
  deactivating: "Deactivating…",
  deactivated: "Deactivated",
  removing: "Removing…",
  removed: "Removed",
  deactivate_failed: "Removal failed",
  remove_failed: "Removal failed",
};

/** True when the removal state is a retryable failure (offer a Retry action). */
function isRemovalFailed(state: ChannexRemovalState | null | undefined): boolean {
  return state === "deactivate_failed" || state === "remove_failed";
}

/** True when the removal is actively in flight (disable conflicting actions). */
function isRemovalInFlight(state: ChannexRemovalState | null | undefined): boolean {
  return state === "deactivating" || state === "deactivated" || state === "removing";
}

/**
 * CARD VISIBILITY RULE for the Connected Channels list — matches the RESTORED
 * backend overview KPI semantics (readiness-gating rollback), so the KPI count
 * and the rendered card count are always consistent (the KPI showed N while the
 * list rendered 0 bug). A connection renders as a Connected Channel card when:
 *   - it has a real provider channel id (channexChannelId non-empty); AND
 *   - its status is live/connected (anything other than not_connected /
 *     disconnected); AND
 *   - it is not a fully 'removed' row (removed rows are physically deleted
 *     server-side; the guard also hides any lingering one).
 * It deliberately does NOT require setup_completed_at / mapping_known /
 * bootstrap_state / mapping completeness — those are display/health signals,
 * never visibility gates. A mid-removal row (deactivating/removing/…_failed)
 * STAYS visible so the operator sees progress / Retry on its own card.
 * PURE + provider-agnostic (no OTA-specific logic).
 */
export function isConnectedChannelCard(c: {
  readonly channexChannelId?: string | null;
  readonly status: ChannexConnectionStatus;
  readonly removalState?: ChannexRemovalState | null;
}): boolean {
  return (
    (c.channexChannelId ?? "").trim() !== "" &&
    c.status !== "not_connected" &&
    c.status !== "disconnected" &&
    c.removalState !== "removed"
  );
}

/**
 * Format an ISO timestamp compactly for "last sync" display, or return the
 * em dash when there is genuinely no value. Guarded against invalid input.
 * Runs only where a Date read is permitted (render is fine post-mount here
 * because the initial render is the pure loading state).
 */
function formatSync(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** Format an ISO timestamp fully (activity timeline), guarded. */
function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** Format an ISO date `YYYY-MM-DD` compactly. */
function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Room/rate-plan mapping display. Per §4/§18: when the Channex mapping state
 * could not be reliably determined (`mappingKnown === false`), we must NOT show
 * a count — a fabricated `0 / 2` is worse than the truth, so we return
 * "Mapping status unavailable". Otherwise: when both totals are 0 AND the
 * connection is not active, we cannot claim a real mapping count, so show "—";
 * else show `mapped / total`. `undefined` mappingKnown is treated as known.
 */
function mappingDisplay(
  mapped: number,
  total: number,
  status: ChannexConnectionStatus,
  mappingKnown?: boolean,
): string {
  if (mappingKnown === false) {
    return "Mapping status unavailable";
  }
  if (total === 0 && status !== "active") {
    return "—";
  }
  return `${mapped} / ${total}`;
}

/**
 * Is this envelope the backend's NOT_READY code? The backend emits `NOT_READY`
 * for the readiness gate, which sits outside the shared `ErrorCode` enum, so we
 * compare the wire string defensively rather than against the narrowed type.
 */
function isNotReady(envelope: { readonly error: { readonly code: string } }): boolean {
  return envelope.error.code === "NOT_READY";
}

/** The display name for a connection: catalogue name, else the OTA code. */
function displayName(
  otaCode: string,
  byCode: ReadonlyMap<string, OtaCatalogueItem>,
): string {
  return byCode.get(otaCode)?.name ?? otaCode;
}

// ===========================================================================
// Client navigation (History API + popstate) — mirrors DashboardScreen.
// ===========================================================================

/** Navigate the console shell to a path and notify listeners (SSR-guarded). */
function navigateTo(path: string): void {
  if (typeof window !== "undefined") {
    window.history.pushState({}, "", path);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}

/** Build the connect URL for an OTA (path only — never a Channex URL/key). */
function connectPath(otaCode: string): string {
  return `${PATH_CONNECT}?ota=${encodeURIComponent(otaCode)}`;
}

// ===========================================================================
// KPI cards
// ===========================================================================

function KpiCards({
  overview,
}: {
  readonly overview: ChannexOverview | null;
}): ReactElement {
  const connected = overview?.connectedChannels ?? 0;
  const active = overview?.activeChannels ?? 0;
  const needsAttention = overview?.needsAttention ?? 0;
  const bookings = overview?.otaBookingsThisMonth ?? 0;
  const dash = overview === null ? "—" : null;
  return (
    <div className="chan2-kpis">
      <div className="console-card">
        <p className="console-card__label">Connected channels</p>
        <p className="console-card__value">{dash ?? connected}</p>
      </div>
      <div className="console-card">
        <p className="console-card__label">Active channels</p>
        <p className="console-card__value">{dash ?? active}</p>
        <p className="console-card__hint">syncing normally</p>
      </div>
      <div
        className={
          needsAttention > 0 ? "console-card console-card--gold" : "console-card"
        }
      >
        <p className="console-card__label">Needs attention</p>
        <p className="console-card__value">{dash ?? needsAttention}</p>
        <p className="console-card__hint">attention or sync error</p>
      </div>
      <div className="console-card">
        <p className="console-card__label">OTA bookings this month</p>
        <p className="console-card__value">{dash ?? bookings}</p>
        <p className="console-card__hint">from AVOS bookings</p>
      </div>
    </div>
  );
}

// ===========================================================================
// Connected channel card
// ===========================================================================

/** The per-card month figure fetched lazily from channel detail. */
interface CardFigures {
  readonly bookingsThisMonth: number | null;
}

function ConnectedCard({
  connection,
  name,
  showProperty,
  propertyName,
  figures,
  removing,
  onManage,
  onContinueSetup,
  onRemove,
}: {
  readonly connection: ChannelConnectionRow;
  readonly name: string;
  readonly showProperty: boolean;
  readonly propertyName: string | null;
  readonly figures: CardFigures | undefined;
  /** True while a remove request for THIS channel is in flight (API pending). */
  readonly removing: boolean;
  readonly onManage: () => void;
  readonly onContinueSetup: () => void;
  readonly onRemove: () => void;
}): ReactElement {
  const status = connection.status;
  const bookings = figures?.bookingsThisMonth;
  const removalState = connection.removalState ?? null;
  // A channel is "in removal" while a request is pending OR the persisted
  // removal_state shows an in-flight / failed removal. Conflicting actions
  // (Manage / Continue setup) are disabled during the operation.
  const inRemoval =
    removing || removalState !== null;
  const removalBusy = removing || isRemovalInFlight(removalState);
  return (
    <article className="console-card chan2-card">
      <div className="chan2-card__head">
        <OtaLogo code={connection.otaCode} name={name} size={40} />
        <div className="chan2-card__ident">
          <h3 className="chan2-card__name">{name}</h3>
          {inRemoval ? (
            <span
              className={
                isRemovalFailed(removalState)
                  ? "avos-status avos-status--critical"
                  : "avos-status avos-status--pending"
              }
            >
              {removing && removalState === null
                ? "Deactivating…"
                : (REMOVAL_LABEL[removalState ?? "deactivating"] ?? "Removing…")}
            </span>
          ) : (
            <span className={statusPill(status)}>{STATUS_LABEL[status]}</span>
          )}
          {showProperty && propertyName !== null ? (
            <p className="chan2-card__prop">{propertyName}</p>
          ) : null}
        </div>
      </div>

      <dl className="chan2-card__grid">
        <div>
          <dt>Rooms mapped</dt>
          <dd>
            {mappingDisplay(
              connection.mappedRooms,
              connection.totalRooms,
              status,
              connection.mappingKnown,
            )}
          </dd>
        </div>
        <div>
          <dt>Rate plans mapped</dt>
          <dd>
            {mappingDisplay(
              connection.mappedRatePlans,
              connection.totalRatePlans,
              status,
              connection.mappingKnown,
            )}
          </dd>
        </div>
        <div>
          <dt>Last successful sync</dt>
          <dd>{formatSync(connection.lastSyncAt)}</dd>
        </div>
        <div>
          <dt>Bookings this month</dt>
          <dd>{bookings === null || bookings === undefined ? "—" : bookings}</dd>
        </div>
      </dl>

      {isRemovalFailed(removalState) && connection.removalError ? (
        <p className="channel-card__alert channel-card__alert--critical">
          {connection.removalError}
        </p>
      ) : connection.lastError !== null ? (
        <p className="channel-card__alert channel-card__alert--critical">
          {connection.lastError}
        </p>
      ) : connection.lastWarning !== null ? (
        <p className="channel-card__alert channel-card__alert--attention">
          {connection.lastWarning}
        </p>
      ) : null}

      <div className="chan2-card__actions">
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onManage}
          disabled={removalBusy}
        >
          Manage
        </button>
        {isInProgress(status) && !inRemoval ? (
          <button
            type="button"
            className="console-btn"
            onClick={onContinueSetup}
          >
            Continue setup
          </button>
        ) : null}
        {isRemovalFailed(removalState) ? (
          <button
            type="button"
            className="console-btn console-btn--danger"
            onClick={onRemove}
            disabled={removing}
          >
            {removing ? "Retrying…" : "Retry removal"}
          </button>
        ) : !inRemoval ? (
          <button
            type="button"
            className="console-btn console-btn--ghost"
            onClick={onRemove}
          >
            Deactivate &amp; remove channel
          </button>
        ) : null}
      </div>
    </article>
  );
}

// ===========================================================================
// Empty state + readiness gate
// ===========================================================================

function EmptyState({
  popular,
  onConnect,
}: {
  readonly popular: readonly OtaCatalogueItem[];
  readonly onConnect: () => void;
}): ReactElement {
  return (
    <section className="console-panel console-empty" aria-label="No channels connected">
      <h2 className="console-panel__title">No channels connected yet</h2>
      <p className="console-lede">
        Connect your property to online travel agencies to keep rates,
        availability and reservations synchronised. You will need existing OTA
        accounts and listings — Channex links them to AVOS.
      </p>
      <div className="console-modal__actions">
        <button
          type="button"
          className="console-btn console-btn--gold"
          onClick={onConnect}
        >
          Connect your first channel
        </button>
      </div>
      {popular.length > 0 ? (
        <div className="chan2-search" aria-hidden="true">
          {popular.slice(0, 6).map((item) => (
            <span key={item.code} className="chan2-card__head">
              <OtaLogo code={item.code} name={item.name} size={40} />
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ReadinessGate({
  readiness,
}: {
  readonly readiness: ReadinessResult;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Property setup required">
      <h2 className="console-panel__title">Property setup required</h2>
      <p className="recon-caption">
        Online travel agencies require a complete property profile. Finish the
        checklist below, then return here to connect your channels.
      </p>
      <ul className="channel-checklist">
        {readiness.items.map((item) => (
          <li
            key={item.field}
            className={
              item.ok
                ? "channel-checklist__item channel-checklist__item--ok"
                : "channel-checklist__item channel-checklist__item--missing"
            }
          >
            <span
              className={
                item.ok
                  ? "avos-status avos-status--ok channel-checklist__icon"
                  : "avos-status avos-status--attention channel-checklist__icon"
              }
              aria-hidden="true"
            >
              {item.ok ? "✓" : "–"}
            </span>
            <span className="channel-checklist__label">{item.label}</span>
          </li>
        ))}
      </ul>
      <p className="recon-caption">
        {readiness.missing.length} item
        {readiness.missing.length === 1 ? "" : "s"} outstanding before this
        property can be distributed.
      </p>
      <div className="console-modal__actions">
        <a
          className="console-btn"
          href="/app/properties"
          onClick={(e) => {
            e.preventDefault();
            navigateTo("/app/properties");
          }}
        >
          Go to Properties
        </a>
      </div>
    </section>
  );
}

// ===========================================================================
// Connect catalogue (opened by "+ Connect channel")
// ===========================================================================

/** Footer action for a catalogue card, based on the real connection status. */
function CatalogueCardFooter({
  status,
  acting,
  onConnect,
  onManage,
}: {
  readonly status: ChannexConnectionStatus;
  readonly acting: boolean;
  readonly onConnect: () => void;
  readonly onManage: () => void;
}): ReactElement {
  if (status === "active") {
    return (
      <div className="chan2-card__actions">
        <span className="avos-status avos-status--ok">Connected ✓</span>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onManage}
        >
          Manage
        </button>
      </div>
    );
  }
  if (isInProgress(status)) {
    return (
      <button
        type="button"
        className="console-btn"
        disabled={acting}
        onClick={onConnect}
      >
        {acting ? "Working…" : "Continue setup"}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="console-btn"
      disabled={acting}
      onClick={onConnect}
    >
      {acting ? "Working…" : "Connect"}
    </button>
  );
}

function CataloguePanel({
  propertyName,
  catalogue,
  connectionsByCode,
  actingCode,
  onConnect,
  onManage,
  onBack,
}: {
  readonly propertyName: string | null;
  readonly catalogue: readonly OtaCatalogueItem[];
  readonly connectionsByCode: ReadonlyMap<string, ChannelConnectionRow>;
  readonly actingCode: string | null;
  readonly onConnect: (item: OtaCatalogueItem) => void;
  readonly onManage: (item: OtaCatalogueItem) => void;
  readonly onBack: () => void;
}): ReactElement {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === "") {
      return catalogue;
    }
    return catalogue.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.code.toLowerCase().includes(q),
    );
  }, [catalogue, query]);

  return (
    <section className="console-panel" aria-label="Connect a channel">
      <div className="channel-panel-head">
        <div>
          <p className="console-page-eyebrow">Connect a channel</p>
          <h2 className="console-panel__title">
            {propertyName !== null
              ? `Connect ${propertyName}`
              : "Connect a channel"}
          </h2>
        </div>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onBack}
        >
          Back to channels
        </button>
      </div>
      <p className="recon-caption">
        Pick an OTA to connect. Channex opens a secure window to sign in and map
        your rooms and rates; AVOS never sees your OTA password.
      </p>

      <div className="chan2-search">
        <input
          type="search"
          placeholder="Search channels…"
          aria-label="Search channels"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="chan2-catalogue">
        {filtered.map((item) => {
          const status = connectionsByCode.get(item.code)?.status ?? "not_connected";
          return (
            <article key={item.code} className="console-card chan2-catcard">
              <div className="chan2-catcard__head">
                <OtaLogo code={item.code} name={item.name} size={44} />
                <h3 className="chan2-catcard__name">{item.name}</h3>
              </div>
              <p className="chan2-catcard__desc">{item.description}</p>
              <div className="chan2-catcard__foot">
                <CatalogueCardFooter
                  status={status}
                  acting={actingCode === item.code}
                  onConnect={() => onConnect(item)}
                  onManage={() => onManage(item)}
                />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/** A simple property-picker step shown before the catalogue when scope is All. */
function PropertyPickerPanel({
  properties,
  onPick,
  onBack,
}: {
  readonly properties: readonly ChannexPropertyOption[];
  readonly onPick: (propertyId: string) => void;
  readonly onBack: () => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Choose a property">
      <div className="channel-panel-head">
        <div>
          <p className="console-page-eyebrow">Connect a channel</p>
          <h2 className="console-panel__title">Which property?</h2>
        </div>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onBack}
        >
          Back to channels
        </button>
      </div>
      <p className="recon-caption">
        Channels connect to one property at a time. Choose which property to
        connect.
      </p>
      <div className="chan2-cards">
        {properties.map((p) => (
          <article key={p.id} className="console-card chan2-card">
            <div className="chan2-card__ident">
              <h3 className="chan2-card__name">{p.name}</h3>
              <p className="chan2-card__prop">
                {p.connectedChannels} connected channel
                {p.connectedChannels === 1 ? "" : "s"}
              </p>
            </div>
            <div className="chan2-card__actions">
              <button
                type="button"
                className="console-btn"
                onClick={() => onPick(p.id)}
              >
                Choose
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ===========================================================================
// Dedicated connect page (full-size embedded Channex iframe)
// ===========================================================================

/**
 * The 5-step onboarding indicator (Connection · Mapping · Existing Bookings ·
 * Inventory Verification · Activation). The active step is derived from the
 * real reconciled `stage` AND the durable `bootstrapState` — never from a
 * click — so it survives refresh/restart. The Existing Bookings + Inventory
 * Verification steps exist to STOP AVOS pushing authoritative availability
 * before historical/future OTA bookings are imported (overbooking safety).
 */
function ConnectSteps({
  stage,
  bootstrapState,
}: {
  readonly stage: ChannexConnectionStage | null;
  readonly bootstrapState?: ChannexBootstrapState | null;
}): ReactElement {
  const steps = [
    "Connection",
    "Mapping",
    "Existing Bookings",
    "Inventory Verification",
    "Activation",
  ] as const;
  // Highest step index that should carry the active styling.
  //   0 Connection · 1 Mapping · 2 Existing Bookings · 3 Inventory Verification
  //   · 4 Activation
  let activeIndex: number;
  switch (stage) {
    case "mapping_required":
      activeIndex = 1;
      break;
    case "ready_to_activate":
    case "active": {
      // Mapping is done; the bootstrap lifecycle drives steps 2–4. An un-set /
      // backward-compat 'active' bootstrap means the property is already live,
      // so the whole flow (incl. Activation) is complete.
      const bs = bootstrapState ?? "active";
      if (bs === "pending") activeIndex = 2;
      else if (bs === "importing" || bs === "needs_attention" || bs === "error")
        activeIndex = 2;
      else if (bs === "inventory_recalculating") activeIndex = 3;
      else if (bs === "ready_to_sync") activeIndex = 3;
      else activeIndex = 4; // active
      break;
    }
    case "needs_attention":
    case "connection_required":
    case null:
    default:
      activeIndex = 0;
      break;
  }
  return (
    <div className="chan2-connect__steps" aria-label="Connection steps">
      {steps.map((label, i) => (
        <span
          key={label}
          className={i <= activeIndex ? "chan2-step chan2-step--active" : "chan2-step"}
        >
          <span className="chan2-step__num" aria-hidden="true">
            {i + 1}
          </span>
          {label}
        </span>
      ))}
    </div>
  );
}

/**
 * The Existing Bookings onboarding card. Shows the DURABLE bootstrap status
 * (Not started / Importing / Imported / Needs attention / Failed) with the
 * evidence AVOS has (imported count, unmapped count, earliest arrival, latest
 * departure) and an "Import future reservations" action. State is backend-
 * driven so it survives refresh/restart. Only shown for channels that can
 * import existing reservations (e.g. Booking.com).
 */
function ExistingBookingsCard({
  bootstrapState,
  importedCount,
  unmappedCount,
  earliestArrival,
  latestDeparture,
  error,
  importing,
  zeroButExpected,
  onImport,
}: {
  readonly bootstrapState: ChannexBootstrapState | null;
  readonly importedCount: number | null;
  readonly unmappedCount: number | null;
  readonly earliestArrival: string | null;
  readonly latestDeparture: string | null;
  readonly error: string | null;
  readonly importing: boolean;
  /** True when parked in needs_attention because zero bookings were found (§6/§7). */
  readonly zeroButExpected: boolean;
  readonly onImport: (declared: ExistingBookingsDeclared) => void;
}): ReactElement {
  const bs = bootstrapState ?? "pending";
  let label: string;
  switch (bs) {
    case "connection":
      label = "Finishing connection…";
      break;
    case "importing":
      label = "Importing…";
      break;
    case "inventory_recalculating":
      label = "Recalculating inventory…";
      break;
    case "ready_to_sync":
      label = "Imported — ready to activate";
      break;
    case "active":
      label = "Imported";
      break;
    case "needs_attention":
      label = "Needs attention";
      break;
    case "error":
      label = "Import failed";
      break;
    case "pending":
    default:
      label = "Not started";
      break;
  }
  const busy = importing || bs === "importing" || bs === "inventory_recalculating";
  // The zero-but-expected case asks the operator to complete Pull Future
  // Bookings inside the existing Channex connection, then Retry Import. In every
  // other not-yet-done state we present the EXPLICIT choice: declare there are
  // no existing future bookings, or import the ones that exist.
  const needsRetry = zeroButExpected || (bs === "needs_attention" && (unmappedCount ?? 0) === 0);
  return (
    <div className="console-card">
      <h3 className="chan2-card__name">Existing reservations</h3>
      <p className="recon-caption">
        Before AVOS publishes availability, we import your existing future
        reservations so a room that is already sold is never reopened. AVOS does
        this for you — you do not need to log into the channel.
      </p>
      <p className="recon-caption">
        Status: <strong>{label}</strong>
      </p>
      {importedCount !== null && importedCount > 0 ? (
        <p className="recon-caption">
          Imported {importedCount} reservation{importedCount === 1 ? "" : "s"}
          {earliestArrival !== null && latestDeparture !== null
            ? ` (${earliestArrival} → ${latestDeparture})`
            : ""}
          .
        </p>
      ) : null}
      {unmappedCount !== null && unmappedCount > 0 ? (
        <p className="recon-caption">
          {unmappedCount} booking{unmappedCount === 1 ? "" : "s"} reference an
          unmapped channel room — map or exclude it before activating.
        </p>
      ) : null}
      {error !== null ? <p className="channel-prep__note">{error}</p> : null}
      {needsRetry ? (
        <>
          <p className="recon-caption">
            No existing reservations were found yet. If your channel has future
            bookings, complete <strong>Pull Future Bookings</strong> in the
            channel connection above, then Retry Import.
          </p>
          <div className="console-modal__actions">
            <button
              type="button"
              className="console-btn"
              disabled={busy}
              onClick={() => onImport("import")}
            >
              {busy ? "Working…" : "Retry Import"}
            </button>
            <button
              type="button"
              className="console-btn console-btn--ghost"
              disabled={busy}
              onClick={() => onImport("none")}
            >
              No existing future bookings
            </button>
          </div>
        </>
      ) : (
        <div className="console-modal__actions">
          <button
            type="button"
            className="console-btn"
            disabled={busy}
            onClick={() => onImport("import")}
          >
            {busy ? "Working…" : "Import existing future bookings"}
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={busy}
            onClick={() => onImport("none")}
          >
            No existing future bookings
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Status/onboarding banner shown above the iframe for Airbnb only. Text is
 * driven purely by the real reconciled `stage`/`channelExists` — never
 * fabricated. When no channel exists yet this is an onboarding note; otherwise
 * it reflects the current stage. Rendered as a plain `console-card` (status,
 * not an error).
 */
function AirbnbConnectBanner({
  channelExists,
  stage,
}: {
  readonly channelExists: boolean;
  readonly stage: ChannexConnectionStage | null;
}): ReactElement {
  if (!channelExists) {
    return (
      <div className="console-card">
        <h3 className="chan2-card__name">No Airbnb connection yet</h3>
        <p className="recon-caption">
          No Airbnb connection exists for this property yet. Create one below to
          connect your Airbnb Host account. Airbnb is the only channel available
          to create here.
        </p>
      </div>
    );
  }
  let heading: string;
  let body: string;
  switch (stage) {
    case "mapping_required":
      heading = "Airbnb connected — mapping required";
      body =
        "Map your Airbnb listing to this property's room type and rate plan to continue.";
      break;
    case "ready_to_activate":
      heading = "Airbnb ready to activate";
      body =
        "Mapping is complete. Activate the channel in the embedded view to go live.";
      break;
    case "active":
      heading = "Airbnb is active";
      body = "This Airbnb connection is live and syncing.";
      break;
    case "needs_attention":
      heading = "Airbnb needs attention";
      body =
        "The provider reported an issue with this connection. Review it in the embedded view below.";
      break;
    case "connection_required":
    case null:
    default:
      heading = "Continue your Airbnb connection";
      body = "Continue the Airbnb setup in the embedded view below.";
      break;
  }
  return (
    <div className="console-card">
      <h3 className="chan2-card__name">{heading}</h3>
      <p className="recon-caption">{body}</p>
    </div>
  );
}

function ConnectPage({
  otaCode,
  otaName,
  propertyName,
  prep,
  showPrereq,
  iframeUrl,
  loading,
  error,
  finishing,
  stage,
  channelExists,
  bootstrapState,
  bootstrapImportedCount,
  bootstrapUnmappedCount,
  bootstrapEarliestArrival,
  bootstrapLatestDeparture,
  bootstrapError,
  bootstrapZeroButExpected,
  canImportExisting,
  bootstrapBusy,
  onImportExisting,
  onDismissPrereq,
  onBack,
  onDone,
}: {
  readonly otaCode: string;
  readonly otaName: string;
  readonly propertyName: string | null;
  readonly prep: ChannelPrep | null;
  readonly showPrereq: boolean;
  readonly iframeUrl: string | null;
  readonly loading: boolean;
  readonly error: UserMessageModel | null;
  readonly finishing: boolean;
  readonly stage: ChannexConnectionStage | null;
  readonly channelExists: boolean;
  readonly bootstrapState: ChannexBootstrapState | null;
  readonly bootstrapImportedCount: number | null;
  readonly bootstrapUnmappedCount: number | null;
  readonly bootstrapEarliestArrival: string | null;
  readonly bootstrapLatestDeparture: string | null;
  readonly bootstrapError: string | null;
  readonly bootstrapZeroButExpected: boolean;
  readonly canImportExisting: boolean;
  readonly bootstrapBusy: boolean;
  readonly onImportExisting: (declared: ExistingBookingsDeclared) => void;
  readonly onDismissPrereq: () => void;
  readonly onBack: () => void;
  readonly onDone: () => void;
}): ReactElement {
  const isAirbnb = otaCode === "airbnb";
  const panelTitle = isAirbnb
    ? `Connect Airbnb Property: ${propertyName ?? "your property"}`
    : `Connect ${otaName}`;
  return (
    <section className="console-panel" aria-label={`Connect ${otaName}`}>
      <p className="chan2-connect__crumb">
        <button type="button" onClick={onBack}>
          Channels
        </button>{" "}
        / Connect
      </p>
      <div className="channel-panel-head">
        <div>
          <p className="console-page-eyebrow">Connect a channel</p>
          <h2 className="console-panel__title">{panelTitle}</h2>
          <p className="recon-caption">
            Connect {propertyName ?? "your property"} to {otaName} through AVOS.
          </p>
        </div>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onBack}
        >
          Back to Channels
        </button>
      </div>

      <ConnectSteps stage={stage} bootstrapState={bootstrapState} />

      {isAirbnb ? (
        <p className="recon-caption">
          Connect your existing Airbnb Host account. AVOS never sees or stores
          your Airbnb password.
        </p>
      ) : null}

      {error !== null ? <UserMessage message={error} severity="error" /> : null}

      {/* Existing Bookings step: shown once mapping is complete for channels
          that can import existing reservations (overbooking safety). Driven by
          the DURABLE bootstrap state so it survives refresh/restart. */}
      {canImportExisting &&
      (stage === "ready_to_activate" || stage === "active") &&
      (bootstrapState === null ||
        bootstrapState === "connection" ||
        bootstrapState === "pending" ||
        bootstrapState === "importing" ||
        bootstrapState === "inventory_recalculating" ||
        bootstrapState === "needs_attention" ||
        bootstrapState === "error" ||
        bootstrapState === "ready_to_sync") ? (
        <ExistingBookingsCard
          bootstrapState={bootstrapState}
          importedCount={bootstrapImportedCount}
          unmappedCount={bootstrapUnmappedCount}
          earliestArrival={bootstrapEarliestArrival}
          latestDeparture={bootstrapLatestDeparture}
          error={bootstrapError}
          importing={bootstrapBusy}
          zeroButExpected={bootstrapZeroButExpected}
          onImport={onImportExisting}
        />
      ) : null}

      {showPrereq && prep !== null && prep.prep.note !== null ? (
        <div className="console-card">
          <h3 className="chan2-card__name">{prep.prep.title}</h3>
          <p className="recon-caption">{prep.prep.intro}</p>
          <p className="channel-prep__note">{prep.prep.note}</p>
          <div className="console-modal__actions">
            <button type="button" className="console-btn" onClick={onDismissPrereq}>
              Continue
            </button>
          </div>
        </div>
      ) : (
        <>
          {isAirbnb ? (
            <AirbnbConnectBanner
              channelExists={channelExists}
              stage={stage}
            />
          ) : null}
          <div className="chan2-connect__framewrap">
            {iframeUrl !== null ? (
              <iframe
                className="chan2-connect__frame"
                src={iframeUrl}
                title={`Connect ${otaName}`}
              />
            ) : null}
            {iframeUrl === null || loading ? (
              <div className="chan2-connect__loading" role="status" aria-live="polite">
                <span>Preparing secure channel connection…</span>
              </div>
            ) : null}
          </div>
        </>
      )}

      <div className="console-modal__actions">
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={finishing}
          onClick={onDone}
        >
          {finishing ? "Finishing…" : "Done / Back to Channels"}
        </button>
      </div>
    </section>
  );
}

// ===========================================================================
// Channel Detail — tabbed in-screen view
// ===========================================================================

const DETAIL_TABS = [
  { key: "overview", label: "Overview" },
  { key: "mapping", label: "Mapping" },
  { key: "bookings", label: "Bookings" },
  { key: "pricing", label: "Pricing & Commission" },
  { key: "activity", label: "Activity" },
] as const;

type DetailTab = (typeof DETAIL_TABS)[number]["key"];

/** Friendly labels for Channex activity operations. */
const OPERATION_LABEL: Record<string, string> = {
  property_provisioned: "Property provisioned",
  channel_activated: "Channel activated",
  availability_update: "Availability updated",
  availability_sync: "Availability synced",
  rate_update: "Rates updated",
  rate_sync: "Rates synced",
  reservation_received: "Reservation received",
  booking_ingested: "Booking ingested",
  sync_warning: "Sync warning",
  sync_error: "Sync error",
  mapping_error: "Mapping error",
  webhook_registered: "Webhooks registered",
};

function operationLabel(operation: string): string {
  return (
    OPERATION_LABEL[operation] ??
    operation.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
  );
}

function activityPill(status: string): string {
  switch (status) {
    case "ok":
    case "success":
    case "processed":
      return "avos-status avos-status--ok";
    case "warning":
      return "avos-status avos-status--attention";
    case "error":
    case "failed":
      return "avos-status avos-status--critical";
    default:
      return "avos-status avos-status--pending";
  }
}

/** A small label/value row for the overview tab. */
function DetailRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): ReactElement {
  return (
    <div className="bookings-drawer__row">
      <span className="bookings-drawer__label">{label}</span>
      <span className="bookings-drawer__value">{value}</span>
    </div>
  );
}

function DetailOverviewTab({
  detail,
  onFixMapping,
}: {
  readonly detail: ChannexChannelDetail;
  readonly onFixMapping: () => void;
}): ReactElement {
  const conn = detail.connection;
  const status = conn?.status ?? "not_connected";
  const active = status === "active";

  // Connection-health checks derived ONLY from real connection data.
  const connectionOk = active;
  const roomsMapped =
    conn !== null && conn.totalRooms > 0 && conn.mappedRooms >= conn.totalRooms;
  const ratesMapped =
    conn !== null &&
    conn.totalRatePlans > 0 &&
    conn.mappedRatePlans >= conn.totalRatePlans;
  const unmappedRooms =
    conn !== null && conn.totalRooms > 0
      ? Math.max(0, conn.totalRooms - conn.mappedRooms)
      : 0;

  const check = (ok: boolean, label: string, key: string): ReactElement => (
    <li key={key} className="channel-onboard__check">
      <span
        className={
          ok ? "avos-status avos-status--ok" : "avos-status avos-status--attention"
        }
        aria-hidden="true"
      >
        {ok ? "✓" : "!"}
      </span>
      <span className="channel-checklist__label">{label}</span>
    </li>
  );

  return (
    <div className="channel-detail__tabpanel">
      <div className="bookings-drawer__pills">
        <span className={statusPill(status)}>{STATUS_LABEL[status]}</span>
      </div>
      <DetailRow label="Connection status" value={STATUS_LABEL[status]} />
      <DetailRow
        label="Activation status"
        value={active ? "Active" : "Not active"}
      />
      <DetailRow
        label="Rooms mapped"
        value={
          conn === null
            ? "—"
            : mappingDisplay(conn.mappedRooms, conn.totalRooms, status, conn.mappingKnown)
        }
      />
      <DetailRow
        label="Rate plans mapped"
        value={
          conn === null
            ? "—"
            : mappingDisplay(conn.mappedRatePlans, conn.totalRatePlans, status, conn.mappingKnown)
        }
      />
      <DetailRow
        label="Last successful sync"
        value={formatSync(conn?.lastSyncAt)}
      />

      <div className="chan2-map__section">
        <h4 className="console-form__title">Connection health</h4>
        <ul className="channel-onboard__checklist">
          {check(connectionOk, "OTA connection active", "chk-conn")}
          {check(roomsMapped, "Rooms mapped", "chk-rooms")}
          {check(ratesMapped, "Rate plans mapped", "chk-rates")}
        </ul>
        {unmappedRooms > 0 ? (
          <div className="channel-unmapped">
            <p className="channel-unmapped__title">
              ⚠ {unmappedRooms} room{unmappedRooms === 1 ? "" : "s"} unmapped
            </p>
            <p className="channel-unmapped__note">
              Complete mapping inside the Channex connection window so this
              channel can sync correctly.
            </p>
            <div className="console-modal__actions">
              <button type="button" className="console-btn" onClick={onFixMapping}>
                Fix mapping
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {conn?.lastError ? (
        <p className="channel-card__alert channel-card__alert--critical">
          {conn.lastError}
        </p>
      ) : conn?.lastWarning ? (
        <p className="channel-card__alert channel-card__alert--attention">
          {conn.lastWarning}
        </p>
      ) : null}
    </div>
  );
}

function DetailMappingTab({
  detail,
  onManageMapping,
  opening,
}: {
  readonly detail: ChannexChannelDetail;
  readonly onManageMapping: () => void;
  readonly opening: boolean;
}): ReactElement {
  const conn = detail.connection;
  const status = conn?.status ?? "not_connected";
  const hasTotals = conn !== null && (conn.totalRooms > 0 || conn.totalRatePlans > 0);
  const roomsComplete =
    conn !== null && conn.totalRooms > 0 && conn.mappedRooms >= conn.totalRooms;
  const ratesComplete =
    conn !== null &&
    conn.totalRatePlans > 0 &&
    conn.mappedRatePlans >= conn.totalRatePlans;
  const complete = roomsComplete && ratesComplete;
  const mappingUnloaded = conn !== null && conn.totalRooms > 0 && status !== "active";

  return (
    <div className="channel-detail__tabpanel">
      <h4 className="console-form__title">Mapping</h4>
      <p className="recon-caption">
        Room and rate-plan mapping happens inside the Channex connection window.
        AVOS reflects the mapping counts the provider reports.
      </p>

      <dl className="channel-card__stats">
        <div className="channel-card__stat">
          <dt>Rooms mapped</dt>
          <dd>
            {conn === null
              ? "—"
              : mappingUnloaded
                ? "Complete connection to load mapping"
                : mappingDisplay(conn.mappedRooms, conn.totalRooms, status, conn.mappingKnown)}
          </dd>
        </div>
        <div className="channel-card__stat">
          <dt>Rate plans mapped</dt>
          <dd>
            {conn === null
              ? "—"
              : mappingUnloaded
                ? "Complete connection to load mapping"
                : mappingDisplay(conn.mappedRatePlans, conn.totalRatePlans, status, conn.mappingKnown)}
          </dd>
        </div>
        <div className="channel-card__stat">
          <dt>Status</dt>
          <dd>
            {!hasTotals ? "—" : complete ? "Complete" : "Incomplete"}
          </dd>
        </div>
      </dl>

      <div className="console-modal__actions">
        <button
          type="button"
          className="console-btn"
          disabled={opening}
          onClick={onManageMapping}
        >
          {opening ? "Opening…" : "Manage mapping"}
        </button>
      </div>
    </div>
  );
}

function DetailBookingsTab({
  rows,
  loading,
}: {
  readonly rows: readonly BookingRow[];
  readonly loading: boolean;
}): ReactElement {
  if (loading) {
    return (
      <p className="console-loading" role="status" aria-live="polite">
        Loading bookings…
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="console-empty">
        No bookings from this channel yet. New reservations appear here once the
        OTA sends them.
      </p>
    );
  }
  return (
    <table className="console-table bookings-table">
      <caption className="console-sr-only">Bookings from this channel</caption>
      <thead>
        <tr>
          <th scope="col">Guest</th>
          <th scope="col">OTA Reference</th>
          <th scope="col">Stay</th>
          <th scope="col">Property / Unit</th>
          <th scope="col">Status</th>
          <th scope="col">Gross</th>
          <th scope="col">OTA Commission</th>
          <th scope="col">Net</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => {
          const gross = Number(b.amountMinor ?? "0");
          const hasCommission =
            b.otaCommissionMinor !== undefined && b.otaCommissionMinor !== null;
          const commission = hasCommission ? Number(b.otaCommissionMinor) : 0;
          return (
            <tr key={b.id}>
              <td>
                <span className="bookings-guest">
                  {b.guestName ?? `Guest ${b.guestId.slice(0, 8)}`}
                </span>
              </td>
              <td>{b.confirmationCode ?? "—"}</td>
              <td>
                <span className="bookings-dates">
                  {formatDate(b.checkIn)} → {formatDate(b.checkOut)}
                </span>
              </td>
              <td>
                {b.propertyName ?? "—"}
                {b.unitName !== null ? ` · ${b.unitName}` : ""}
              </td>
              <td>
                <span
                  className={
                    b.status === "confirmed"
                      ? "avos-status avos-status--ok"
                      : b.status === "cancelled"
                        ? "avos-status avos-status--critical"
                        : "avos-status avos-status--pending"
                  }
                >
                  {b.status}
                </span>
              </td>
              <td className="recon-num">{formatMoney(gross, b.currency)}</td>
              <td className="recon-num">
                {hasCommission ? formatMoney(commission, b.currency) : "—"}
              </td>
              <td className="recon-num">
                {hasCommission ? formatMoney(gross - commission, b.currency) : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DetailPricingTab({
  detail,
}: {
  readonly detail: ChannexChannelDetail;
}): ReactElement {
  const currency = FALLBACK_CURRENCY;
  const gross = Number(detail.grossThisMonthMinor ?? "0");
  const commission = Number(detail.commissionThisMonthMinor ?? "0");
  const hasCommission = detail.commissionRows > 0;
  const net = gross - commission;
  const effectivePct =
    hasCommission && gross > 0 ? (commission / gross) * 100 : null;
  return (
    <div className="channel-detail__tabpanel">
      <dl className="channel-card__stats">
        <div className="channel-card__stat">
          <dt>Gross this month</dt>
          <dd>{formatMoney(gross, currency)}</dd>
        </div>
        <div className="channel-card__stat">
          <dt>OTA commission</dt>
          <dd>{hasCommission ? formatMoney(commission, currency) : "—"}</dd>
        </div>
        <div className="channel-card__stat channel-detail__pricing-figure">
          <dt>Effective commission %</dt>
          <dd>
            {effectivePct !== null ? `${effectivePct.toFixed(1)}%` : "—"}
          </dd>
        </div>
        <div className="channel-card__stat">
          <dt>Net this month</dt>
          <dd>{hasCommission ? formatMoney(net, currency) : "—"}</dd>
        </div>
      </dl>
      <p className="recon-caption">
        Effective commission is calculated from actual booking data where the OTA
        supplies commission.
      </p>
    </div>
  );
}

function DetailActivityTab({
  activity,
  loading,
}: {
  readonly activity: readonly ChannexActivityRow[];
  readonly loading: boolean;
}): ReactElement {
  if (loading) {
    return (
      <p className="console-loading" role="status" aria-live="polite">
        Loading activity…
      </p>
    );
  }
  if (activity.length === 0) {
    return <p className="console-empty">No activity recorded yet.</p>;
  }
  return (
    <ol className="channel-timeline">
      {activity.map((a) => (
        <li key={a.id} className="channel-timeline__item">
          <div className="channel-timeline__head">
            <span className="channel-timeline__op">
              {operationLabel(a.operation)}
            </span>
            <span className={activityPill(a.status)}>{a.status}</span>
          </div>
          <p className="channel-timeline__time">{formatDateTime(a.startedAt)}</p>
          {a.errorSummary ? (
            <p className="channel-timeline__error">{a.errorSummary}</p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** The Channel Detail container — loads real detail, activity and bookings. */
function ChannelDetail({
  otaCode,
  otaName,
  propertyId,
  selectedPropertyId,
  onBack,
  onManageMapping,
  mappingOpening,
}: {
  readonly otaCode: string;
  readonly otaName: string;
  /** The property this channel belongs to (from the connection row). */
  readonly propertyId: string;
  /** The landing scope (null = All) — used to client-filter bookings. */
  readonly selectedPropertyId: string | null;
  readonly onBack: () => void;
  readonly onManageMapping: () => void;
  readonly mappingOpening: boolean;
}): ReactElement {
  const [tab, setTab] = useState<DetailTab>("overview");
  const [detail, setDetail] = useState<ChannexChannelDetail | null>(null);
  const [detailError, setDetailError] = useState<UserMessageModel | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [bookings, setBookings] = useState<readonly BookingRow[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [activity, setActivity] = useState<readonly ChannexActivityRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // The property scope used for detail/activity: the channel's own property.
  const scope = propertyId;

  useEffect(() => {
    let active = true;
    setDetailLoading(true);
    setDetailError(null);
    apiChannexChannelDetail({ otaCode, propertyId: scope })
      .then((result) => {
        if (!active) return;
        const err = asError(result);
        if (err !== null) {
          setDetail(null);
          setDetailError(toUserMessage(err));
        } else {
          setDetail(result as ChannexChannelDetail);
        }
        setDetailLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setDetail(null);
        setDetailError({
          what: "Could not load channel detail.",
          why: "The request to the channel service failed.",
          next: "Try refreshing in a moment.",
        });
        setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [otaCode, scope]);

  // Bookings from the AVOS register, filtered by OTA name then property scope.
  useEffect(() => {
    let active = true;
    setBookingsLoading(true);
    apiListBookings({ channel: otaName })
      .then((result) => {
        if (!active) return;
        const rows = result.rows.filter((r) =>
          selectedPropertyId === null
            ? true
            : r.propertyId === selectedPropertyId,
        );
        setBookings(rows);
        setBookingsLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setBookings([]);
        setBookingsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [otaName, selectedPropertyId]);

  useEffect(() => {
    let active = true;
    setActivityLoading(true);
    apiChannexActivity({ otaCode, propertyId: scope })
      .then((rows) => {
        if (!active) return;
        setActivity(rows);
        setActivityLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setActivity([]);
        setActivityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [otaCode, scope]);

  return (
    <section
      className="console-panel channel-detail"
      aria-label={`${otaName} detail`}
    >
      <div className="channel-panel-head">
        <div className="chan2-card__head">
          <OtaLogo code={otaCode} name={otaName} size={40} />
          <div>
            <p className="console-page-eyebrow">Channel</p>
            <h2 className="console-panel__title">{otaName}</h2>
          </div>
        </div>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          onClick={onBack}
        >
          Back to channels
        </button>
      </div>

      {detailError !== null ? (
        <UserMessage message={detailError} severity="error" />
      ) : null}

      <div
        className="channel-detail__tabs"
        role="tablist"
        aria-label="Channel detail sections"
      >
        {DETAIL_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`channel-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`channel-tabpanel-${t.key}`}
            className={
              tab === t.key
                ? "channel-detail__tab channel-detail__tab--active"
                : "channel-detail__tab"
            }
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`channel-tabpanel-${tab}`}
        aria-labelledby={`channel-tab-${tab}`}
      >
        {detailLoading ? (
          <p className="console-loading" role="status" aria-live="polite">
            Loading channel…
          </p>
        ) : detail === null ? (
          <p className="console-empty">
            Channel detail is unavailable right now.
          </p>
        ) : (
          <>
            {tab === "overview" ? (
              <DetailOverviewTab detail={detail} onFixMapping={onManageMapping} />
            ) : null}
            {tab === "mapping" ? (
              <DetailMappingTab
                detail={detail}
                onManageMapping={onManageMapping}
                opening={mappingOpening}
              />
            ) : null}
            {tab === "bookings" ? (
              <DetailBookingsTab rows={bookings} loading={bookingsLoading} />
            ) : null}
            {tab === "pricing" ? <DetailPricingTab detail={detail} /> : null}
            {tab === "activity" ? (
              <DetailActivityTab activity={activity} loading={activityLoading} />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

/** How often to reconcile + refresh while on the landing (§39). */
const REFRESH_INTERVAL_MS = 45_000;

// ===========================================================================
// Integration diagnostics (local developer/admin surface, §24)
// ===========================================================================

/** Pill class for the API-connectivity descriptor. Never surfaces the key. */
function connectivityPill(connectivity: string): string {
  switch (connectivity) {
    case "connected":
    case "configured":
      return "avos-status avos-status--ok";
    case "failed":
      return "avos-status avos-status--critical";
    case "mock":
    default:
      return "avos-status avos-status--pending";
  }
}

/** Outcome → { cls, label } for an actionable booking event badge. */
function eventOutcomePill(outcome: string): { cls: string; label: string } {
  switch (outcome) {
    case "mapping_required":
      return { cls: "avos-status avos-status--attention", label: "Mapping required" };
    case "inventory_conflict":
      return { cls: "avos-status avos-status--attention", label: "Inventory conflict" };
    case "failed":
      return { cls: "avos-status avos-status--critical", label: "Failed" };
    default:
      return { cls: "avos-status avos-status--pending", label: outcome };
  }
}

/**
 * A collapsible local integration-diagnostics panel. Self-contained: it loads
 * diagnostics + actionable booking events on first render, and offers the
 * connectivity test / manual sync / availability-push actions. Never renders
 * secrets — only the descriptors returned by the backend.
 */
function DiagnosticsPanel({
  onSynced,
}: {
  readonly onSynced: () => void;
}): ReactElement {
  const [diagnostics, setDiagnostics] = useState<ChannexDiagnostics | null>(null);
  const [events, setEvents] = useState<readonly ChannexBookingEvent[] | null>(null);

  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const [syncMsg, setSyncMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [syncing, setSyncing] = useState(false);

  const [availMsg, setAvailMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [verifying, setVerifying] = useState(false);

  const [invMsg, setInvMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [syncingInv, setSyncingInv] = useState(false);

  const [reprocessing, setReprocessing] = useState<string | null>(null);

  const refreshDiagnostics = useCallback(async (): Promise<void> => {
    try {
      const d = await apiChannexDiagnostics();
      setDiagnostics(d);
    } catch {
      // Non-fatal: leave the previous snapshot in place.
    }
  }, []);

  const refreshEvents = useCallback(async (): Promise<void> => {
    try {
      const list = await apiChannexBookingEvents({ actionableOnly: true });
      setEvents(list);
    } catch {
      setEvents([]);
    }
  }, []);

  // Load once when the panel is first rendered (i.e. first expand).
  useEffect(() => {
    void refreshDiagnostics();
    void refreshEvents();
  }, [refreshDiagnostics, refreshEvents]);

  const onTest = useCallback(async (): Promise<void> => {
    setTesting(true);
    setTestMsg(null);
    try {
      const res = await apiChannexTestConnection();
      setTestMsg({ ok: res.connected, text: res.message });
    } catch {
      setTestMsg({ ok: false, text: "Connection test failed." });
    } finally {
      setTesting(false);
    }
  }, []);

  const onSync = useCallback(async (): Promise<void> => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await apiChannexSyncBookings();
      const err = asError(res);
      if (err !== null) {
        setSyncMsg({ ok: false, text: err.error.message });
      } else {
        const s = (res as BookingSyncResult).bookings;
        setSyncMsg({
          ok: true,
          text: `Fetched ${s.fetched} · Created ${s.created} · Modified ${s.modified} · Cancelled ${s.cancelled} · Duplicates ${s.duplicates} · Mapping required ${s.mappingRequired}`,
        });
        await refreshDiagnostics();
        await refreshEvents();
        onSynced();
      }
    } catch {
      setSyncMsg({ ok: false, text: "Booking sync failed." });
    } finally {
      setSyncing(false);
    }
  }, [refreshDiagnostics, refreshEvents, onSynced]);

  const onVerify = useCallback(async (): Promise<void> => {
    setVerifying(true);
    setAvailMsg(null);
    try {
      const res = await apiChannexProcessAvailability();
      const err = asError(res);
      if (err !== null) {
        setAvailMsg({ ok: false, text: err.error.message });
      } else {
        const r = res as { succeeded: number; failed: number };
        setAvailMsg({
          ok: r.failed === 0,
          text: `Pushed: succeeded ${r.succeeded}, failed ${r.failed}`,
        });
        await refreshDiagnostics();
      }
    } catch {
      setAvailMsg({ ok: false, text: "Availability push failed." });
    } finally {
      setVerifying(false);
    }
  }, [refreshDiagnostics]);

  const onSyncInventory = useCallback(async (): Promise<void> => {
    setSyncingInv(true);
    setInvMsg(null);
    try {
      const res = await apiChannexSyncInventory();
      const err = asError(res);
      if (err !== null) {
        setInvMsg({ ok: false, text: err.error.message });
      } else {
        const results = (res as { results: readonly FullSyncResultRow[] }).results ?? [];
        const avail = results.reduce((n, r) => n + r.availabilityPushed, 0);
        const rates = results.reduce((n, r) => n + r.ratesPushed, 0);
        const restr = results.reduce((n, r) => n + r.restrictionsPushed, 0);
        setInvMsg({
          ok: true,
          text:
            results.length === 0
              ? "No provisioned property to sync."
              : `Full ARI sync: ${results.length} propert${results.length === 1 ? "y" : "ies"} · availability ${avail} · rates ${rates} · restrictions ${restr}`,
        });
        await refreshDiagnostics();
        onSynced();
      }
    } catch {
      setInvMsg({ ok: false, text: "Full inventory sync failed." });
    } finally {
      setSyncingInv(false);
    }
  }, [refreshDiagnostics, onSynced]);

  const onReprocess = useCallback(
    async (revisionId: string): Promise<void> => {
      setReprocessing(revisionId);
      try {
        await apiChannexReprocessBookingEvent(revisionId);
      } catch {
        // Non-fatal; the refreshed list reflects the current state.
      } finally {
        await refreshEvents();
        await refreshDiagnostics();
        setReprocessing(null);
      }
    },
    [refreshEvents, refreshDiagnostics],
  );

  const statusLine = (msg: { ok: boolean; text: string } | null): ReactElement | null =>
    msg === null ? null : (
      <p className={msg.ok ? "recon-caption" : "channel-card__alert channel-card__alert--attention"}>
        {msg.text}
      </p>
    );

  return (
    <details className="console-panel">
      <summary>Integration diagnostics (local)</summary>

      <div className="console-card-grid">
        <div className="console-card">
          <p className="console-card__label">Environment</p>
          <p className="console-card__value">{diagnostics?.environment ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">API connectivity</p>
          {diagnostics !== null ? (
            <span className={connectivityPill(diagnostics.apiConnectivity)}>
              {diagnostics.apiConnectivity}
            </span>
          ) : (
            <p className="console-card__value">—</p>
          )}
        </div>
        <div className="console-card">
          <p className="console-card__label">Mapped properties</p>
          <p className="console-card__value">{diagnostics?.mappedProperties ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Mapped room types</p>
          <p className="console-card__value">{diagnostics?.mappedRoomTypes ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Mapped rate plans</p>
          <p className="console-card__value">{diagnostics?.mappedRatePlans ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Unacknowledged revisions</p>
          <p className="console-card__value">{diagnostics?.unacknowledgedRevisions ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Pending availability jobs</p>
          <p className="console-card__value">{diagnostics?.pendingAvailabilityJobs ?? "—"}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Last booking sync</p>
          <p className="console-card__value">{formatSync(diagnostics?.lastBookingSyncAt)}</p>
        </div>
        <div className="console-card">
          <p className="console-card__label">Last availability sync</p>
          <p className="console-card__value">{formatSync(diagnostics?.lastAvailabilitySyncAt)}</p>
        </div>
      </div>

      <div className="console-modal__actions">
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={testing}
          onClick={() => void onTest()}
        >
          {testing ? "Testing…" : "Test API connection"}
        </button>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={syncing}
          onClick={() => void onSync()}
        >
          {syncing ? "Syncing…" : "Sync bookings now"}
        </button>
        <button
          type="button"
          className="console-btn console-btn--ghost"
          disabled={verifying}
          onClick={() => void onVerify()}
        >
          {verifying ? "Verifying…" : "Verify availability"}
        </button>
        <button
          type="button"
          className="console-btn"
          disabled={syncingInv}
          onClick={() => void onSyncInventory()}
        >
          {syncingInv ? "Syncing inventory…" : "Sync full inventory & rates"}
        </button>
      </div>
      {statusLine(testMsg)}
      {statusLine(syncMsg)}
      {statusLine(availMsg)}
      {statusLine(invMsg)}

      <h3 className="console-panel__title">Actionable booking issues</h3>
      {events === null ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading booking issues…
        </p>
      ) : events.length === 0 ? (
        <p className="console-empty">
          No booking issues. All OTA revisions applied cleanly.
        </p>
      ) : (
        <div className="chan2-cards">
          {events.map((ev) => {
            const badge = eventOutcomePill(ev.outcome);
            const reference = ev.otaReservationCode ?? ev.channexBookingId ?? "—";
            return (
              <article key={ev.id} className="console-card">
                <div className="chan2-card__ident">
                  <h4 className="chan2-card__name">{ev.otaName ?? "OTA"}</h4>
                  <span className={badge.cls}>{badge.label}</span>
                </div>
                <p className="recon-caption">{reference}</p>
                {ev.errorSummary !== null ? (
                  <p className="channel-card__alert channel-card__alert--attention">
                    {ev.errorSummary}
                  </p>
                ) : null}
                <div className="chan2-card__actions">
                  <button
                    type="button"
                    className="console-btn console-btn--ghost"
                    disabled={reprocessing === ev.channexRevisionId}
                    onClick={() => void onReprocess(ev.channexRevisionId)}
                  >
                    {reprocessing === ev.channexRevisionId ? "Reprocessing…" : "Reprocess"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </details>
  );
}

/** The landing dataset for the current scope (property or All). */
interface LandingData {
  readonly catalogue: readonly OtaCatalogueItem[];
  readonly connections: readonly ChannelConnectionRow[];
  readonly overview: ChannexOverview | null;
}

type LandingState =
  | { readonly kind: "loading" }
  | { readonly kind: "gate"; readonly readiness: ReadinessResult }
  | { readonly kind: "ready"; readonly data: LandingData }
  | { readonly kind: "error" };

/** The catalogue flow sub-state (which panel of the connect journey shows). */
type CatalogueStage = "none" | "pick-property" | "catalogue";

/** Read `?ota=` from a path's query string (SSR-safe: caller passes the path). */
function readOtaQuery(search: string): string | null {
  const q = new URLSearchParams(search);
  const ota = q.get("ota");
  return ota !== null && ota !== "" ? ota : null;
}

export default function ChannelsScreen(): ReactElement {
  // --- Routing state (seeded in an effect; never read window at render) -----
  const [path, setPath] = useState<string>(PATH_LANDING);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    const sync = (): void => {
      setPath(window.location.pathname);
      setSearch(window.location.search);
    };
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  // --- Property selector -----------------------------------------------------
  const [properties, setProperties] = useState<readonly ChannexPropertyOption[]>(
    [],
  );
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    apiChannexProperties()
      .then((rows) => {
        if (!active) return;
        setProperties(rows);
        // Auto-select when exactly one property; else default to All (null).
        if (rows.length === 1) {
          setSelectedPropertyId(rows[0]!.id);
        }
        setPropertiesLoaded(true);
      })
      .catch(() => {
        if (!active) return;
        setProperties([]);
        setPropertiesLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const singleProperty = properties.length === 1;
  const propertyName = useMemo(() => {
    if (selectedPropertyId === null) return null;
    return properties.find((p) => p.id === selectedPropertyId)?.name ?? null;
  }, [properties, selectedPropertyId]);

  // --- Landing data ----------------------------------------------------------
  const [state, setState] = useState<LandingState>({ kind: "loading" });
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // --- Detail + catalogue + connect flow state ------------------------------
  const [selected, setSelected] = useState<{
    otaCode: string;
    otaName: string;
    propertyId: string;
  } | null>(null);
  const [catalogueStage, setCatalogueStage] = useState<CatalogueStage>("none");
  const [actingCode, setActingCode] = useState<string | null>(null);
  const [mappingOpening, setMappingOpening] = useState(false);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);

  // --- Deactivate + Remove Channel flow -------------------------------------
  // The channel targeted by the open confirmation modal (null = closed).
  const [removeOta, setRemoveOta] = useState<{
    code: string;
    name: string;
    propertyId: string;
  } | null>(null);
  // The ota code whose remove request is currently in flight (disables actions).
  const [removingOta, setRemovingOta] = useState<string | null>(null);

  // --- Connect page state ---------------------------------------------------
  const [connectOta, setConnectOta] = useState<{
    code: string;
    name: string;
    propertyId: string;
  } | null>(null);
  const [connectPrep, setConnectPrep] = useState<ChannelPrep | null>(null);
  const [connectShowPrereq, setConnectShowPrereq] = useState(false);
  const [connectUrl, setConnectUrl] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<UserMessageModel | null>(
    null,
  );
  const [connectFinishing, setConnectFinishing] = useState(false);
  const [connectStage, setConnectStage] = useState<ChannexConnectionStage | null>(
    null,
  );
  const [connectChannelExists, setConnectChannelExists] = useState(false);
  // Durable existing-reservation bootstrap state for the OTA being connected.
  const [connectBootstrapState, setConnectBootstrapState] =
    useState<ChannexBootstrapState | null>(null);
  const [connectBootstrapImported, setConnectBootstrapImported] = useState<number | null>(null);
  const [connectBootstrapUnmapped, setConnectBootstrapUnmapped] = useState<number | null>(null);
  const [connectBootstrapEarliest, setConnectBootstrapEarliest] = useState<string | null>(null);
  const [connectBootstrapLatest, setConnectBootstrapLatest] = useState<string | null>(null);
  const [connectBootstrapError, setConnectBootstrapError] = useState<string | null>(null);
  const [connectBootstrapZeroButExpected, setConnectBootstrapZeroButExpected] =
    useState(false);
  const [connectBootstrapBusy, setConnectBootstrapBusy] = useState(false);

  const onConnectPage = path === PATH_CONNECT;

  // Load the landing dataset for the current scope.
  const load = useCallback(
    async (scope: string | null): Promise<void> => {
      const scopeArg = scope ?? undefined;
      // Readiness gate only applies to a specific property.
      if (scope !== null) {
        const readiness = await apiChannexReadiness(scopeArg);
        if (asError(readiness) === null) {
          const gate = readiness as ReadinessResult;
          if (gate.ready === false) {
            setState({ kind: "gate", readiness: gate });
            return;
          }
        }
      }
      const [catalogue, overviewRaw] = await Promise.all([
        apiChannexCatalogue(scopeArg),
        apiChannexOverview(scopeArg),
      ]);
      const overview =
        asError(overviewRaw) === null ? (overviewRaw as ChannexOverview) : null;
      setState({
        kind: "ready",
        data: {
          catalogue: catalogue.catalogue,
          connections: catalogue.connections,
          overview,
        },
      });
    },
    [],
  );

  // Initial + scope-change load.
  useEffect(() => {
    if (!propertiesLoaded) {
      return;
    }
    let active = true;
    setState({ kind: "loading" });
    load(selectedPropertyId)
      .then(() => {
        if (active) setLastRefreshed(new Date().toISOString());
      })
      .catch(() => {
        if (active) setState({ kind: "error" });
      });
    return () => {
      active = false;
    };
  }, [propertiesLoaded, selectedPropertyId, load]);

  // Reconcile against the live provider, then refresh (§39).
  const reconcileAndRefresh = useCallback(async (): Promise<void> => {
    setRefreshing(true);
    try {
      await apiChannexReconcile(selectedPropertyId ?? undefined);
      await load(selectedPropertyId);
      setLastRefreshed(new Date().toISOString());
    } catch {
      // Leave the current state; a transient reconcile failure is non-fatal.
    } finally {
      setRefreshing(false);
    }
  }, [selectedPropertyId, load]);

  // Apply a connection row's bootstrap fields to the connect-page bootstrap
  // state (keeps the Existing Bookings card + stepper backend-driven).
  const applyConnectionBootstrap = useCallback(
    (conn: ChannelConnectionRow | null | undefined): void => {
      setConnectBootstrapState(conn?.bootstrapState ?? null);
      setConnectBootstrapImported(conn?.bootstrapImportedCount ?? null);
      setConnectBootstrapUnmapped(conn?.bootstrapUnmappedCount ?? null);
      setConnectBootstrapEarliest(conn?.bootstrapEarliestArrival ?? null);
      setConnectBootstrapLatest(conn?.bootstrapLatestDeparture ?? null);
      setConnectBootstrapError(conn?.bootstrapError ?? null);
      // A connection row does not carry the zero-but-expected flag directly;
      // it is derived from the durable status refresh (refreshConnectBootstrap).
      setConnectBootstrapZeroButExpected(false);
    },
    [],
  );

  // Refresh the durable bootstrap status from the backend (survives refresh).
  const refreshConnectBootstrap = useCallback(
    async (otaCode: string, propertyId: string): Promise<void> => {
      const status = await apiChannexBootstrapStatus({ otaCode, propertyId });
      if (asError(status) !== null) return;
      const s = status as ChannexBootstrapStatus;
      setConnectBootstrapState(s.state);
      setConnectBootstrapImported(s.importedCount);
      setConnectBootstrapUnmapped(s.unmappedCount);
      setConnectBootstrapEarliest(s.earliestArrival);
      setConnectBootstrapLatest(s.latestDeparture);
      setConnectBootstrapError(s.error);
      setConnectBootstrapZeroButExpected(s.zeroButExpected === true);
    },
    [],
  );

  // The operator-triggered bootstrap import. The explicit zero-booking choice
  // (§6/§7) is passed through: 'import' (pull existing future reservations) or
  // 'none' (declare there are none → bootstrap may complete with zero).
  const onImportExisting = useCallback(
    async (declared: ExistingBookingsDeclared): Promise<void> => {
    const ota = connectOta;
    if (ota === null || ota.propertyId === null) return;
    setConnectBootstrapBusy(true);
    try {
      const r = await apiChannexBootstrapImport({
        otaCode: ota.code,
        propertyId: ota.propertyId,
        existingBookingsDeclared: declared,
      });
      const rErr = asError(r);
      if (rErr !== null) {
        setConnectError(toUserMessage(rErr));
      } else {
        const res = r as ChannexBootstrapImportResult;
        setConnectBootstrapState(res.state);
        setConnectBootstrapImported(res.imported);
        setConnectBootstrapUnmapped(res.unmapped);
        setConnectBootstrapEarliest(res.earliestArrival);
        setConnectBootstrapLatest(res.latestDeparture);
        setConnectBootstrapError(res.error);
        setConnectBootstrapZeroButExpected(res.zeroButExpected === true);
      }
    } catch {
      setConnectError({
        what: "Could not import existing reservations.",
        why: "The request to the channel service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setConnectBootstrapBusy(false);
    }
    },
    [connectOta],
  );

  // Periodic reconcile while on the landing (not on the connect page).
  useEffect(() => {
    if (onConnectPage || state.kind !== "ready") {
      return;
    }
    const id = window.setInterval(() => {
      void reconcileAndRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [onConnectPage, state.kind, reconcileAndRefresh]);

  // Derived: catalogue + connection lookups.
  const byCode: ReadonlyMap<string, OtaCatalogueItem> = useMemo(() => {
    if (state.kind !== "ready") return new Map();
    return new Map(state.data.catalogue.map((c) => [c.code, c] as const));
  }, [state]);

  const connectionsByCode: ReadonlyMap<string, ChannelConnectionRow> = useMemo(() => {
    if (state.kind !== "ready") return new Map();
    return new Map(state.data.connections.map((c) => [c.otaCode, c] as const));
  }, [state]);

  // The Connected Channels list — card visibility matches the RESTORED backend
  // overview KPI semantics (readiness-gating rollback): a connection is a
  // Connected Channel when it has a real provider channel id AND a live/connected
  // status (anything other than not_connected/disconnected). It deliberately
  // does NOT gate on setup_completed_at / mapping_known / bootstrap_state /
  // mapping completeness — those are display/health signals only, not visibility
  // requirements. The backend KPI stopped using them, so the card list must too,
  // otherwise the KPI shows N while the list renders 0 (the reported bug).
  // A channel mid-removal (deactivating/removing/…_failed) STAYS on its card so
  // the operator sees progress / Retry (the card renders the transient state and
  // disables conflicting actions); the healthy-vs-removing distinction is per
  // card. Only a fully 'removed' row (physically deleted server-side; the filter
  // also hides any lingering one) is dropped.
  const connectedRows: readonly ChannelConnectionRow[] = useMemo(() => {
    if (state.kind !== "ready") return [];
    return state.data.connections.filter((c) => isConnectedChannelCard(c));
  }, [state]);

  // Per-card "bookings this month" figures, fetched lazily from channel detail.
  const [cardFigures, setCardFigures] = useState<
    ReadonlyMap<string, CardFigures>
  >(new Map());

  useEffect(() => {
    if (connectedRows.length === 0) {
      setCardFigures(new Map());
      return;
    }
    let active = true;
    const next = new Map<string, CardFigures>();
    void Promise.all(
      connectedRows.map(async (conn) => {
        try {
          const result = await apiChannexChannelDetail({
            otaCode: conn.otaCode,
            propertyId: conn.propertyId,
          });
          if (asError(result) === null) {
            const d = result as ChannexChannelDetail;
            next.set(conn.id, { bookingsThisMonth: d.bookingsThisMonth });
          } else {
            next.set(conn.id, { bookingsThisMonth: null });
          }
        } catch {
          next.set(conn.id, { bookingsThisMonth: null });
        }
      }),
    ).then(() => {
      if (active) setCardFigures(next);
    });
    return () => {
      active = false;
    };
  }, [connectedRows]);

  // Resolve the property to connect to (explicit scope or single property).
  const resolveConnectProperty = useCallback((): string | null => {
    if (selectedPropertyId !== null) return selectedPropertyId;
    if (singleProperty) return properties[0]!.id;
    return null;
  }, [selectedPropertyId, singleProperty, properties]);

  // Open the catalogue (property-picker first when scope is All + multi-prop).
  const handleOpenCatalogue = useCallback((): void => {
    setApiError(null);
    setStatus(null);
    setSelected(null);
    if (resolveConnectProperty() === null) {
      setCatalogueStage("pick-property");
    } else {
      setCatalogueStage("catalogue");
    }
  }, [resolveConnectProperty]);

  // Pick a property in the picker step → scope to it and show the catalogue.
  const handlePickProperty = useCallback((propertyId: string): void => {
    setSelectedPropertyId(propertyId);
    setCatalogueStage("catalogue");
  }, []);

  /**
   * Begin connecting an OTA: provision if needed, mint a connect token, then
   * navigate to the dedicated connect page. Surfaces NOT_READY as the readiness
   * gate and ErrorEnvelopes as messages (never opens an iframe on failure).
   */
  const handleConnect = useCallback(
    async (item: OtaCatalogueItem): Promise<void> => {
      const property = resolveConnectProperty();
      if (property === null) {
        setCatalogueStage("pick-property");
        return;
      }
      setApiError(null);
      setStatus(null);
      setActingCode(item.code);
      // Clear any stage/host-account context from a previous OTA so a stale
      // banner never flashes before the fresh connect result arrives.
      setConnectStage(null);
      setConnectChannelExists(false);
      try {
        const provision = await apiChannexProvision(property);
        const provisionErr = asError(provision);
        if (provisionErr !== null) {
          if (isNotReady(provisionErr)) {
            const readiness = await apiChannexReadiness(property);
            if (asError(readiness) === null) {
              setCatalogueStage("none");
              setState({ kind: "gate", readiness: readiness as ReadinessResult });
              return;
            }
          }
          setApiError(toUserMessage(provisionErr));
          return;
        }
        void (provision as ProvisionResult);

        // Fetch prep copy (optional prerequisite card) before showing the page.
        let prep: ChannelPrep | null = null;
        const prepResult = await apiChannexPrep({ otaCode: item.code, propertyId: property });
        if (asError(prepResult) === null) {
          prep = prepResult as ChannelPrep;
        }

        // Mint the connect token/URL from the backend.
        const connect = await apiChannexConnect({ otaCode: item.code, propertyId: property });
        const connectErr = asError(connect);
        if (connectErr !== null) {
          if (isNotReady(connectErr)) {
            const readiness = await apiChannexReadiness(property);
            if (asError(readiness) === null) {
              setCatalogueStage("none");
              setState({ kind: "gate", readiness: readiness as ReadinessResult });
              return;
            }
          }
          setApiError(toUserMessage(connectErr));
          return;
        }
        const result = connect as ConnectResult;

        // Prepare the connect page and navigate to it.
        setConnectOta({ code: item.code, name: item.name, propertyId: property });
        setConnectPrep(prep);
        setConnectShowPrereq(prep !== null && prep.prep.note !== null);
        setConnectStage(result.stage ?? null);
        setConnectChannelExists(result.channelExists ?? false);
        applyConnectionBootstrap(result.connection);
        setConnectError(null);
        if (result.iframe !== null) {
          setConnectUrl(result.iframe.url);
        } else {
          setConnectUrl(null);
          setConnectError({
            what: `${item.name} could not open a secure connection window.`,
            why: "The provider did not return a connection URL (it may be unavailable in this environment).",
            next: "Try again shortly, or contact support if it persists.",
          });
        }
        setCatalogueStage("none");
        navigateTo(connectPath(item.code));
      } catch {
        setApiError({
          what: "Could not start the connection.",
          why: "The request to the channel service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setActingCode(null);
      }
    },
    [resolveConnectProperty],
  );

  // Open the Manage / Channel Detail view for an OTA.
  const handleManage = useCallback(
    (otaCode: string, otaName: string, connPropertyId: string): void => {
      setApiError(null);
      setStatus(null);
      setCatalogueStage("none");
      setSelected({ otaCode, otaName, propertyId: connPropertyId });
    },
    [],
  );

  // Confirm the Safe Deactivate + Remove: FIRST deactivate at Channex, THEN
  // remove, and only after Channex confirms removal is the local row deleted.
  // All channel calls go through services.ts → the dev API → the core provider
  // (never a fetch from React). Idempotent — a Retry re-invokes this handler.
  const handleRemoveChannel = useCallback(
    async (otaCode: string, propertyId: string): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setRemoveOta(null);
      setRemovingOta(otaCode);
      try {
        const result = await apiChannexRemoveChannel({ otaCode, propertyId });
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
        } else {
          const res = result as ChannelRemoveResult;
          if (res.finalState === "removed") {
            setStatus({
              what: "Channel removed.",
              why: "AVOS deactivated the channel in Channex, then removed the connection. Existing reservations and guest history are retained.",
              next: "You can reconnect this OTA later as a new connection.",
            });
          } else {
            // deactivate_failed / remove_failed → surface the real provider
            // error; the card shows a Retry that re-calls this route.
            setApiError({
              what:
                res.finalState === "deactivate_failed"
                  ? "Could not deactivate the channel in Channex."
                  : "Could not remove the channel from Channex.",
              why: res.error ?? "The provider reported an error.",
              next: "Retry on the channel card. Existing reservations and history are untouched.",
            });
          }
        }
      } catch {
        setApiError({
          what: "Could not remove the channel.",
          why: "The request to the channel service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setRemovingOta(null);
        // Reconcile + refresh so the card reflects the real persisted state
        // (and a removed channel drops out of the list + count).
        await load(selectedPropertyId).catch(() => undefined);
        setLastRefreshed(new Date().toISOString());
      }
    },
    [load, selectedPropertyId],
  );

  // Open the embedded connect/mapping page for the currently selected channel.
  const handleManageMapping = useCallback(async (): Promise<void> => {
    if (selected === null) return;
    setMappingOpening(true);
    setApiError(null);
    try {
      const connect = await apiChannexConnect({
        otaCode: selected.otaCode,
        propertyId: selected.propertyId,
      });
      const connectErr = asError(connect);
      if (connectErr !== null) {
        setApiError(toUserMessage(connectErr));
        return;
      }
      const result = connect as ConnectResult;
      setConnectOta({
        code: selected.otaCode,
        name: selected.otaName,
        propertyId: selected.propertyId,
      });
      setConnectPrep(null);
      setConnectShowPrereq(false);
      setConnectError(
        result.iframe === null
          ? {
              what: "Mapping window is unavailable.",
              why: "The provider did not return a mapping URL for this channel.",
              next: "Try again shortly, or contact support if it persists.",
            }
          : null,
      );
      setConnectUrl(result.iframe?.url ?? null);
      setSelected(null);
      navigateTo(connectPath(selected.otaCode));
    } catch {
      setApiError({
        what: "Could not open the mapping window.",
        why: "The request to the channel service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setMappingOpening(false);
    }
  }, [selected]);

  // Leave the connect page: reconcile the real provider state, then go back.
  const handleConnectDone = useCallback(async (): Promise<void> => {
    setConnectFinishing(true);
    try {
      await apiChannexReconcile(
        connectOta?.propertyId ?? selectedPropertyId ?? undefined,
      );
      await load(selectedPropertyId);
      setLastRefreshed(new Date().toISOString());
    } catch {
      // Non-fatal — still return to the landing.
    } finally {
      setConnectFinishing(false);
      setConnectUrl(null);
      setConnectOta(null);
      setConnectPrep(null);
      setConnectShowPrereq(false);
      setConnectError(null);
      setConnectStage(null);
      setConnectChannelExists(false);
      navigateTo(PATH_LANDING);
    }
  }, [connectOta, selectedPropertyId, load]);

  // Back from the connect page without finishing.
  const handleConnectBack = useCallback((): void => {
    setConnectUrl(null);
    setConnectOta(null);
    setConnectPrep(null);
    setConnectShowPrereq(false);
    setConnectError(null);
    setConnectStage(null);
    setConnectChannelExists(false);
    navigateTo(PATH_LANDING);
  }, []);

  // If the user lands directly on /app/channels/connect (deep link / reload)
  // without a prepared connect context, resolve the OTA from ?ota= and try to
  // open a fresh connection so the page is never blank.
  useEffect(() => {
    if (!onConnectPage || connectOta !== null || !propertiesLoaded) {
      return;
    }
    const otaCode = readOtaQuery(search);
    if (otaCode === null) {
      return;
    }
    // Best-effort: only proceed once the catalogue (for the name) is known.
    if (state.kind !== "ready") {
      return;
    }
    const property = resolveConnectProperty();
    if (property === null) {
      return;
    }
    const name = displayName(otaCode, byCode);
    let active = true;
    setConnectLoading(true);
    setConnectError(null);
    void (async () => {
      try {
        const connect = await apiChannexConnect({ otaCode, propertyId: property });
        if (!active) return;
        const err = asError(connect);
        if (err !== null) {
          setConnectOta({ code: otaCode, name, propertyId: property });
          setConnectUrl(null);
          setConnectError(toUserMessage(err));
          return;
        }
        const result = connect as ConnectResult;
        setConnectOta({ code: otaCode, name, propertyId: property });
        setConnectUrl(result.iframe?.url ?? null);
        setConnectStage(result.stage ?? null);
        setConnectChannelExists(result.channelExists ?? false);
        applyConnectionBootstrap(result.connection);
        // Refresh the durable bootstrap status directly (survives a page reload
        // where the connect result's connection row may be stale).
        void refreshConnectBootstrap(otaCode, property);
        if (result.iframe === null) {
          setConnectError({
            what: `${name} could not open a secure connection window.`,
            why: "The provider did not return a connection URL.",
            next: "Try again shortly, or contact support if it persists.",
          });
        }
      } catch {
        if (active) {
          setConnectOta({ code: otaCode, name, propertyId: property });
          setConnectUrl(null);
          setConnectError({
            what: "Could not open the connection window.",
            why: "The request to the channel service failed.",
            next: "Check the API server is running and try again.",
          });
        }
      } finally {
        if (active) setConnectLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [
    onConnectPage,
    connectOta,
    propertiesLoaded,
    search,
    state.kind,
    resolveConnectProperty,
    byCode,
    applyConnectionBootstrap,
    refreshConnectBootstrap,
  ]);

  // Popular catalogue subset for the empty state (first few, backend-ordered).
  const popular: readonly OtaCatalogueItem[] =
    state.kind === "ready" ? state.data.catalogue : [];

  // Whether the OTA being connected can import existing/future reservations
  // (drives the Existing Bookings onboarding step). Derived from the catalogue
  // capability flags — never fabricated.
  const connectCanImport: boolean = (() => {
    const code = connectOta?.code;
    if (code === undefined) return false;
    const caps = byCode.get(code)?.capabilities;
    return (
      caps?.supportsFutureBookingPull === true ||
      caps?.supportsExistingBookingImport === true
    );
  })();

  const refreshedLabel = lastRefreshed !== null ? formatSync(lastRefreshed) : "—";

  // -------------------------------------------------------------------------
  // Connect page view (kept inside the shell content area).
  // -------------------------------------------------------------------------
  if (onConnectPage) {
    return (
      <section aria-labelledby="channels-heading" data-console-screen="channels">
        <p className="console-page-eyebrow">Distribution · Channels</p>
        <h1 id="channels-heading">Channels</h1>
        <ConnectPage
          otaCode={connectOta?.code ?? ""}
          otaName={connectOta?.name ?? "channel"}
          propertyName={propertyName}
          prep={connectPrep}
          showPrereq={connectShowPrereq}
          iframeUrl={connectUrl}
          loading={connectLoading}
          error={connectError}
          finishing={connectFinishing}
          stage={connectStage}
          channelExists={connectChannelExists}
          bootstrapState={connectBootstrapState}
          bootstrapImportedCount={connectBootstrapImported}
          bootstrapUnmappedCount={connectBootstrapUnmapped}
          bootstrapEarliestArrival={connectBootstrapEarliest}
          bootstrapLatestDeparture={connectBootstrapLatest}
          bootstrapError={connectBootstrapError}
          bootstrapZeroButExpected={connectBootstrapZeroButExpected}
          canImportExisting={connectCanImport}
          bootstrapBusy={connectBootstrapBusy}
          onImportExisting={(declared) => void onImportExisting(declared)}
          onDismissPrereq={() => setConnectShowPrereq(false)}
          onBack={handleConnectBack}
          onDone={() => void handleConnectDone()}
        />
      </section>
    );
  }

  // -------------------------------------------------------------------------
  // Landing view.
  // -------------------------------------------------------------------------
  return (
    <section aria-labelledby="channels-heading" data-console-screen="channels">
      <p className="console-page-eyebrow">Distribution · Channels</p>
      <h1 id="channels-heading">Channels</h1>
      <p className="console-lede">
        Connect your properties to online travel agencies and keep rates,
        availability and reservations synchronised.
      </p>

      {apiError !== null ? (
        <UserMessage message={apiError} severity="error" />
      ) : null}
      {status !== null ? (
        <UserMessage message={status} severity="status" />
      ) : null}

      {/* Header: property selector (left) + primary Connect CTA (right). */}
      <div className="chan2-header">
        <div className="chan2-header__controls">
          {!singleProperty ? (
            <div className="chan2-propsel">
              <label htmlFor="chan2-property">Property</label>
              <select
                id="chan2-property"
                value={selectedPropertyId ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedPropertyId(v === "" ? null : v);
                  setSelected(null);
                  setCatalogueStage("none");
                }}
              >
                <option value="">All properties</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="console-btn console-btn--gold"
          onClick={handleOpenCatalogue}
        >
          + Connect channel
        </button>
      </div>

      {/* Refresh caption + manual refresh (§39). */}
      {state.kind === "ready" ? (
        <p className="recon-caption chan2-connect__crumb">
          Last refreshed {refreshedLabel}
          {"  "}
          <button
            type="button"
            onClick={() => void reconcileAndRefresh()}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </p>
      ) : null}

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading channels…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Channel data is unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "gate" ? (
        <ReadinessGate readiness={state.readiness} />
      ) : null}

      {state.kind === "ready" ? (
        <>
          <KpiCards overview={state.data.overview} />

          {selected !== null ? (
            <ChannelDetail
              otaCode={selected.otaCode}
              otaName={selected.otaName}
              propertyId={selected.propertyId}
              selectedPropertyId={selectedPropertyId}
              onBack={() => setSelected(null)}
              onManageMapping={() => void handleManageMapping()}
              mappingOpening={mappingOpening}
            />
          ) : catalogueStage === "pick-property" ? (
            <PropertyPickerPanel
              properties={properties}
              onPick={handlePickProperty}
              onBack={() => setCatalogueStage("none")}
            />
          ) : catalogueStage === "catalogue" ? (
            <CataloguePanel
              propertyName={propertyName}
              catalogue={state.data.catalogue}
              connectionsByCode={connectionsByCode}
              actingCode={actingCode}
              onConnect={(item) => void handleConnect(item)}
              onManage={(item) => {
                const conn = connectionsByCode.get(item.code);
                handleManage(
                  item.code,
                  item.name,
                  conn?.propertyId ?? resolveConnectProperty() ?? "",
                );
              }}
              onBack={() => setCatalogueStage("none")}
            />
          ) : connectedRows.length === 0 ? (
            <EmptyState popular={popular} onConnect={handleOpenCatalogue} />
          ) : (
            <section className="console-panel" aria-label="Connected channels">
              <h2 className="console-panel__title">Connected channels</h2>
              <div className="chan2-cards">
                {connectedRows.map((conn) => {
                  const name = displayName(conn.otaCode, byCode);
                  const propName =
                    properties.find((p) => p.id === conn.propertyId)?.name ??
                    null;
                  return (
                    <ConnectedCard
                      key={conn.id}
                      connection={conn}
                      name={name}
                      showProperty={selectedPropertyId === null}
                      propertyName={propName}
                      figures={cardFigures.get(conn.id)}
                      removing={removingOta === conn.otaCode}
                      onRemove={() => {
                        // A persisted *_failed state retries immediately; a
                        // healthy channel opens the confirmation modal first.
                        if (isRemovalFailed(conn.removalState)) {
                          void handleRemoveChannel(conn.otaCode, conn.propertyId);
                        } else {
                          setRemoveOta({
                            code: conn.otaCode,
                            name,
                            propertyId: conn.propertyId,
                          });
                        }
                      }}
                      onManage={() =>
                        handleManage(conn.otaCode, name, conn.propertyId)
                      }
                      onContinueSetup={() => {
                        setConnectOta({
                          code: conn.otaCode,
                          name,
                          propertyId: conn.propertyId,
                        });
                        void handleConnect(
                          byCode.get(conn.otaCode) ?? {
                            code: conn.otaCode,
                            name,
                            description: "",
                          },
                        );
                      }}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Local integration diagnostics (§24) — landing view only, at the
              very bottom, and never on a detail/catalogue sub-view. */}
          {selected === null && catalogueStage === "none" ? (
            <DiagnosticsPanel onSynced={() => void reconcileAndRefresh()} />
          ) : null}
        </>
      ) : null}

      {/* Deactivate + Remove Channel confirmation modal. Reuses the shared
          .console-modal--confirm pattern; the final CTA is the ONLY danger
          action. The flow deactivates in Channex first, then removes, and never
          deletes reservations/guest history. */}
      {removeOta !== null ? (
        <div
          className="console-modal-scrim"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRemoveOta(null);
          }}
        >
          <div
            className="console-modal console-modal--confirm"
            role="dialog"
            aria-modal="true"
            aria-label={`Deactivate and remove ${removeOta.name}`}
          >
            <header className="console-modal__header">
              <h2 className="console-modal__title">
                Deactivate and remove {removeOta.name}?
              </h2>
              <button
                type="button"
                className="console-btn console-btn--ghost console-modal__close"
                aria-label="Close"
                onClick={() => setRemoveOta(null)}
              >
                ✕
              </button>
            </header>
            <div className="console-modal__body">
              <ul className="console-list">
                <li>
                  AVOS first deactivates the channel in Channex so it stops
                  selling.
                </li>
                <li>
                  After deactivation succeeds, AVOS removes the channel
                  connection from Channex.
                </li>
                <li>
                  Existing reservations and guest/history data in AVOS are NOT
                  deleted.
                </li>
                <li>
                  You can reconnect this OTA later as a new connection.
                </li>
              </ul>
              <div className="console-modal__actions">
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={() => setRemoveOta(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="console-btn console-btn--danger"
                  onClick={() =>
                    void handleRemoveChannel(removeOta.code, removeOta.propertyId)
                  }
                  disabled={removingOta === removeOta.code}
                >
                  {removingOta === removeOta.code
                    ? "Working…"
                    : "Deactivate & remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
